import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";

import { format } from "date-fns";
import { z } from 'zod';

import { Botao, Card, Cores, Corpo, FormularioCabecalho, FormularioGrid, FormularioLabel, FormularioRodape, FormularioSectionTitle, Icone, Titulo } from "@componentes-mu/componentes-mu";
import { zodResolver } from '@hookform/resolvers/zod';

import { FlexColumn, FlexRow, } from "../../../components/Flex";
import { TicketReferenciaExterna } from "../../../types/tickets/types";
import styles from './styles.module.scss';
import { InputFile } from "../../../components/InputFile";
import { InputTextArea } from "../../../components/InputTextArea/InputTextArea";
import thumbUpIcon from '../../../assets/icons/thumb_up.svg';
import thumbDownIcon from '../../../assets/icons/thumb_down.svg';
import { FeedbackFilters, PesquisaSatisfacao, RespostaCliente, TipoAtendimento } from "../../../types/cliente/feedback/feedback";
import { RatingScale } from "../../../components/RatingScale";
import { InputCheckbox } from "../../../components/InputCheckbox";


const respostaSchema = z.object({
    idPergunta: z.number(),
    resposta: z.nativeEnum(RespostaCliente).optional(),
    motivos: z.array(z.number()),
})

const schema = z.object({
    numeroFicha: z.number()
        .refine((val) => val !== undefined && val !== null, {
            message: "Número da ficha é obrigatório",
        }),

    concluido: z.boolean().optional()
        .refine((val) => val !== undefined && val !== null, {
            message: "O campo concluído é obrigatório",
        }),

    comentario: z.string().optional(),

    tipoAtendimento: z.nativeEnum(TipoAtendimento)
        .refine((val) => val !== undefined && val !== null, {
            message: "O tipo de atendimento é obrigatório",
        }),

    respostas: z
        .array(respostaSchema)
        .superRefine((respostas, ctx) => {

            if (respostas.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Pelo menos uma resposta deve ser fornecida",
                    path: [],
                });

                return;
            }

            respostas.forEach((resposta, index) => {
                if (!resposta.resposta) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Resposta obrigatória",
                        path: [index, "resposta"],
                    });
                }

                if (!resposta.motivos || resposta.motivos.length === 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Selecione pelo menos um motivo",
                        path: [index, "motivos"],
                    });
                }
            })
        })
})

export type FeedbackProps = {
    ticket: TicketReferenciaExterna;
    form: FeedbackFilters
    files: File[];
    questions: PesquisaSatisfacao;
    onGoBack: () => void;
    onCreate: (data: FeedbackFilters) => void
    onCleanFields: () => void
    onRemoveFile: (index: number) => void;
    onClearAllFiles?: () => void;
    onFilesChange: (files: File[]) => void;
}

export const Feedback = ({ ticket, form, files, questions, onFilesChange, onGoBack, onCreate, onCleanFields, onRemoveFile, onClearAllFiles }: FeedbackProps) => {

    const { setValue, watch, handleSubmit, formState: { errors }, reset } = useForm<FeedbackFilters>({
        defaultValues: {
            ...form,
            respostas: questions.perguntas.map((pergunta) => ({
                idPergunta: pergunta.oid,
                motivos: [],
            })),
        },
        resolver: zodResolver(schema),
    });

    useEffect(() => {
        reset({
            ...form,
            respostas: questions.perguntas.map((pergunta) => ({
                idPergunta: pergunta.oid,
                motivos: [],
            })),
        });
    }, [form, questions, reset]);

    const [openAccordions, setOpenAccordions] = useState<number[]>(
        questions.perguntas.map((_, index) => index)
    );

    const toggleAccordion = (index: number) => {
        setOpenAccordions(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    return (
        <form onSubmit={handleSubmit(onCreate)} className={styles.formContainer}>

            <FlexColumn className={styles.headerButtons}>
                <Botao tipoComportamento="button" onClick={onGoBack} estilo="outlined">
                    <Corpo tipo="button1" cor={Cores.CoresIdentidadeMU.azulMu} negrito={700}>
                        Voltar
                    </Corpo>
                </Botao>
            </FlexColumn>

            <div className={styles.cardFeedback}>
                <Card
                    style={{ padding: '4px 0px 4px 0px', rowGap: '0' }}
                    corDeFundo={Cores.GradacaoNeutros.CinzaFillMU200}
                    titulo={
                        <FormularioCabecalho>
                            <Titulo tipo="cabecalho3" negrito={true} cor={Cores.CoresIdentidadeMU.cinzaMu}>
                                Retorno Cliente
                            </Titulo>
                            <Corpo tipo="overline" negrito={400} cor={Cores.GradacaoPrimaria.AzulMU500}>
                                Feedback, melhorias ou sugestões
                            </Corpo>
                        </FormularioCabecalho>
                    }
                    corpo={
                        <>
                            <FormularioGrid tipo="container">
                                <FormularioSectionTitle style={{ backgroundColor: Cores.GradacaoNeutros.CinzaFillMU200 }}>
                                    <Corpo tipo="label3" negrito={700} cor={Cores.GradacaoNeutros.CinzaFillMU600}>
                                        DADOS DO TICKET
                                    </Corpo>
                                </FormularioSectionTitle>
                                <FormularioGrid
                                    tipo="row"
                                    style={{ backgroundColor: Cores.GradacaoNeutros.CinzaFillMU100 }}
                                >
                                    <FormularioGrid tipo="column" width={33}>
                                        <FormularioLabel
                                            cssLayout="FULL_WIDTH"
                                            conteudo={
                                                <Corpo tipo="body2" negrito={500}>
                                                    Número Ticket:
                                                </Corpo>
                                            }
                                        >
                                            <Corpo tipo="body2" negrito={500} cor={Cores.GradacaoNeutros.CinzaFillMU500}>
                                                {ticket.ticket}
                                            </Corpo>

                                        </FormularioLabel>
                                    </FormularioGrid>
                                    <FormularioGrid tipo="column" width={33}>
                                        <FormularioLabel
                                            cssLayout="FULL_WIDTH"
                                            conteudo={
                                                <Corpo tipo="body2" negrito={500}>
                                                    Responsável:
                                                </Corpo>
                                            }
                                        >
                                            <Corpo tipo="body2" negrito={500} cor={Cores.GradacaoNeutros.CinzaFillMU500}>
                                                {ticket.responsavel}
                                            </Corpo>
                                        </FormularioLabel>
                                    </FormularioGrid>
                                    <FormularioGrid tipo="column" width={33}>
                                        <FormularioLabel
                                            cssLayout="FULL_WIDTH"
                                            conteudo={
                                                <Corpo tipo="body2" negrito={500}>
                                                    Criado Por:
                                                </Corpo>
                                            }
                                        >
                                            <Corpo tipo="body2" negrito={500} cor={Cores.GradacaoNeutros.CinzaFillMU500}>
                                                {ticket.criadoPor}
                                            </Corpo>
                                        </FormularioLabel>
                                    </FormularioGrid>

                                    <FormularioGrid tipo="column" width={33}>
                                        <FormularioLabel
                                            cssLayout="FULL_WIDTH"
                                            conteudo={
                                                <Corpo tipo="body2" negrito={500}>
                                                    Versão:
                                                </Corpo>
                                            }
                                        >
                                            <Corpo tipo="body2" negrito={500} cor={Cores.GradacaoNeutros.CinzaFillMU500}>
                                                {ticket.versao}
                                            </Corpo>

                                        </FormularioLabel>
                                    </FormularioGrid>
                                    <FormularioGrid tipo="column" width={33}>
                                        <FormularioLabel
                                            cssLayout="FULL_WIDTH"
                                            conteudo={
                                                <Corpo tipo="body2" negrito={500}>
                                                    Inclusão:
                                                </Corpo>
                                            }
                                        >
                                            <Corpo tipo="body2" negrito={500} cor={Cores.GradacaoNeutros.CinzaFillMU500}>
                                                {format(new Date(ticket.inclusao), 'dd/MM/yyyy | HH:mm')}
                                            </Corpo>
                                        </FormularioLabel>
                                    </FormularioGrid>
                                    <FormularioGrid tipo="column" width={33}>
                                        <FormularioLabel
                                            cssLayout="FULL_WIDTH"
                                            conteudo={
                                                <Corpo tipo="body2" negrito={500}>
                                                    Tipo:
                                                </Corpo>
                                            }
                                        >
                                            <Corpo tipo="body2" negrito={500} cor={Cores.GradacaoNeutros.CinzaFillMU500}>
                                                {ticket.tipo}
                                            </Corpo>
                                        </FormularioLabel>
                                    </FormularioGrid>
                                </FormularioGrid>

                                <FormularioSectionTitle
                                    style={{
                                        backgroundColor: Cores.GradacaoNeutros.CinzaFillMU200,
                                    }}
                                >
                                    <Corpo tipo="label3" negrito={700} cor={Cores.GradacaoNeutros.CinzaFillMU600}>
                                        SOLICITAÇÃO
                                    </Corpo>
                                </FormularioSectionTitle>

                                <FormularioGrid
                                    tipo="row"
                                    style={{
                                        backgroundColor: Cores.GradacaoNeutros.CinzaFillMU100,
                                    }}
                                >

                                    <FlexColumn gap={"1rem"} style={{ padding: "2rem 3rem" }}>
                                        <FlexColumn gap={"1rem"}>
                                            <Corpo tipo="body2" negrito={500} cor={errors.concluido ? Cores.MensagensDoSistema.VermelhoMU : Cores.GradacaoNeutros.PretoMU900}>
                                                Como você avalia a solução apresentada?
                                            </Corpo>

                                            <FlexRow gap={"1rem"} justifyContent="flex-start">
                                                <div className={`${styles.estiloCard} ${styles.ativo} ${watch('concluido') == false ? styles.inativo : ''}`}
                                                    onClick={() => setValue('concluido', true)}>
                                                    <img src={thumbUpIcon} alt="Excelente" className={styles.estiloIcone} />
                                                    <div className={styles.estiloTexto}>Resolvido</div>
                                                </div>

                                                <div className={`${styles.estiloCard} ${styles.ativoNao} ${watch('concluido') == true ? styles.inativo : ''}`}
                                                    onClick={() => setValue('concluido', false)}>
                                                    <img src={thumbDownIcon} alt="Não Foi Resolvido" className={styles.estiloIcone} />
                                                    <div className={styles.estiloTexto}>Não Foi Resolvido</div>
                                                </div>
                                            </FlexRow>
                                        </FlexColumn>

                                        <FlexColumn gap="1rem">
                                            {questions.perguntas.map((question, index) => {
                                                const response = watch(`respostas.${index}.resposta`);
                                                const open = openAccordions.includes(index);
                                                const error = !!errors.respostas?.message;
                                                const errorQuestion = errors.respostas?.[index];

                                                return (
                                                    <FlexColumn
                                                        key={question.oid}
                                                        style={{
                                                            border: '4px solid #dadada',
                                                            borderRadius: 8,
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleAccordion(index)}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px 16px',
                                                                cursor: 'pointer',
                                                                backgroundColor: '#dadada',
                                                                border: 'none',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                textAlign: 'left'
                                                            }}
                                                        >
                                                            <Corpo tipo="body2" negrito={500}
                                                                cor={(errorQuestion || error) ? Cores.MensagensDoSistema.VermelhoMU : Cores.GradacaoNeutros.PretoMU900}>
                                                                {question.descricao}
                                                            </Corpo>


                                                            <Icone
                                                                nome={open ? 'expand_less' : 'expand_more'}
                                                                cor={Cores.CoresIdentidadeMU.azulMu}
                                                                altura="18px"
                                                                largura="18px"
                                                            />

                                                        </button>


                                                        {open && (
                                                            <FlexColumn gap="2rem" style={{ padding: '12px 16px' }}>
                                                                <FlexRow gap="1rem" justifyContent="flex-start">
                                                                    <RatingScale
                                                                        value={response}
                                                                        onChange={(valor) => {
                                                                            setValue(`respostas.${index}.idPergunta`, question.oid);
                                                                            setValue(`respostas.${index}.resposta`, valor);
                                                                        }}
                                                                    />
                                                                </FlexRow>


                                                                <FlexRow gap="0.5rem" alignItems="center" justifyContent="flex-start" >
                                                                    <Corpo tipo="body2" negrito={500}>
                                                                        Motivos:
                                                                    </Corpo>
                                                                </FlexRow>


                                                                <FlexColumn gap="8px">
                                                                    {question.motivosPerguntas.map((motivo) => {
                                                                        const motivosSelecionados =
                                                                            watch(`respostas.${index}.motivos`) ?? [];

                                                                        const checked = motivosSelecionados.includes(motivo.oid);

                                                                        return (
                                                                            <InputCheckbox
                                                                                key={motivo.oid}
                                                                                label={motivo.descricao}
                                                                                checked={checked}
                                                                                onChange={(isChecked) => {
                                                                                    const novosMotivos = isChecked
                                                                                        ? [...motivosSelecionados, motivo.oid]
                                                                                        : motivosSelecionados.filter(id => id !== motivo.oid);

                                                                                    setValue(`respostas.${index}.motivos`, novosMotivos);
                                                                                }}
                                                                            />
                                                                        );
                                                                    })}
                                                                </FlexColumn>
                                                            </FlexColumn>
                                                        )}
                                                    </FlexColumn>
                                                );
                                            })}
                                        </FlexColumn>

                                        <FlexColumn>

                                            <Corpo tipo="body2" negrito={500} cor={errors.comentario ? Cores.MensagensDoSistema.VermelhoMU : Cores.GradacaoNeutros.PretoMU900}>
                                                Comentário:
                                            </Corpo>

                                            <InputTextArea
                                                id="descricao"
                                                placeholder="Deixe aqui seus comentários, sugestões ou elogios..."
                                                onChange={(e) => setValue("comentario", e || '')}
                                                valor={watch('comentario')}
                                                error={Boolean(errors.comentario)}
                                                infoText={errors.comentario?.message}
                                            />

                                        </FlexColumn>

                                        <FlexRow gap={"1rem"} justifyContent="flex-start" alignItems="center">

                                            <Corpo tipo="body2" negrito={500}>Anexo:</Corpo>

                                            <div style={{ maxWidth: "200px" }}>
                                                <InputFile
                                                    files={files}
                                                    multiple
                                                    label="Selecionar arquivos"
                                                    onFilesChange={onFilesChange}
                                                    onRemoveFile={onRemoveFile}
                                                    onClearAll={onClearAllFiles}
                                                />
                                            </div>


                                        </FlexRow>

                                    </FlexColumn>
                                </FormularioGrid>
                            </FormularioGrid>
                        </>}
                    rodape={
                        <FormularioRodape>
                            <Botao
                                tamanho="medium"
                                tipo="tipoDefault"
                                estilo="outlined"
                                tipoComportamento="button"
                                onClick={() => {
                                    onCleanFields()
                                    reset()
                                }}
                            >
                                Limpar campos
                            </Botao>

                            <Botao
                                tamanho="medium"
                                tipo="tipoDefault"
                                estilo="primaryDark"
                                tipoComportamento="submit"
                            >
                                Cadastrar
                            </Botao>
                        </FormularioRodape>
                    }
                >

                </Card>
            </div>

        </form >
    )
}